import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { shiftActions } from "../slices/shift";
import { ShiftMapper } from "../../mappers/ShiftMapper";
import { getErrorMessage } from "../../utils/errorUtils";

import { ShiftOpenRequestDto } from "../../shared/dtos/shift/ShiftOpenRequestDto";
import { ShiftResponseDto } from "../../shared/dtos/shift/ShiftResponseDto";
import { ShiftSummaryDto } from "../../shared/dtos/shift/ShiftSummaryDto";
import { DateRangePaginationQueryDto } from "../../shared/dtos/common/DateRangePaginationQueryDto";
import { UpdateShiftClosePayload } from "../../shared/interfaces/shift/UpdateShiftClosePayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getShifts =
  (params?: DateRangePaginationQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(shiftActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<ShiftSummaryDto>>
      >("/shifts", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(shiftActions.setShifts(data.data.content));
        dispatch(shiftActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(shiftActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getShiftById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(shiftActions.setLoadingFetchCurrent());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<ShiftResponseDto>
      >(`/shifts/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(shiftActions.setCurrentShift(data.data));
      }
    } catch (error) {
      dispatch(shiftActions.setErrorFetchCurrent(getErrorMessage(error)));
    }
  };

export const getCurrentOrRecentShift =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(shiftActions.setLoadingFetchCurrent());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<ShiftResponseDto>
      >("/shifts/current-recent", {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(shiftActions.setCurrentShift(data.data));
      }
    } catch (error) {
      dispatch(shiftActions.setErrorFetchCurrent(getErrorMessage(error)));
    }
  };

export const checkCashierHasOpenShift =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(shiftActions.setLoadingHasOpenShift());
    try {
      const { data } = await axiosInstance.get<ApiResponseDto<boolean>>(
        "/shifts/has-open-shift",
        {
          headers: { requiresAuth: true },
        },
      );
      if (
        data.status === ApiResponseStatus.SUCCESS &&
        data.data !== undefined
      ) {
        dispatch(shiftActions.setHasOpenShift(data.data));
      }
    } catch (error) {
      dispatch(shiftActions.setHasOpenShift(false));
    }
  };

export const openShift =
  (values: ShiftOpenRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(shiftActions.setLoadingOpen());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<ShiftResponseDto>
      >("/shifts/open", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Shift opened successfully!");
        const summary = ShiftMapper.mapShiftResponseToSummary(data.data);
        dispatch(shiftActions.openShiftSuccess(summary));
        dispatch(
          shiftActions.setServerStatus({
            operation: "open",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(shiftActions.setErrorOpen(message));
    }
  };

export const closeShift =
  (payload: UpdateShiftClosePayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(shiftActions.setLoadingClose());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<ShiftResponseDto>
      >(`/shifts/close/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Shift closed and reconciled!");
        const summary = ShiftMapper.mapShiftResponseToSummary(data.data);
        dispatch(shiftActions.closeShiftSuccess(summary));
        dispatch(
          shiftActions.setServerStatus({
            operation: "close",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(shiftActions.setErrorClose(message));
    }
  };

export const resetShiftServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(shiftActions.setServerStatus(null));
